import { motion } from 'framer-motion'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  ArrowRight,
  BookOpen,
  Zap,
  Database,
  Bot,
  Check,
  ChevronRight,
  ArrowUp,
  MessageSquare,
  Plug,
  Clock,
  Sparkles,
  Terminal,
  Globe,
  Cpu,
  Link2,
  CircleDot,
  Gift,
  Coins,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Header } from '@/components/header'
import { Logo } from '@/components/logo'
import { useState, useEffect } from 'react'
import { ContactSalesDialog } from '@/components/landing/ContactSalesDialog'
import { useCallPackPricing } from '@/lib/api/hooks'

const WELCOME_CREDITS = 200

const LandingPageV2 = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [showContactSales, setShowContactSales] = useState(false)

  const { data: callPacksData } = useCallPackPricing()
  const creditProduct = callPacksData?.[0]
  const creditPrice = creditProduct?.price ?? 5
  const creditUnit = creditProduct?.pack_size ?? 100

  useEffect(() => {
    const hash = location.hash
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      }, 100)
    }
  }, [location.hash])

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleStartFreeTrial = () => {
    navigate('/auth?mode=signup')
  }

  const handleViewDocumentation = () => {
    window.open('https://docs.ecosemantic.com', '_blank', 'noopener,noreferrer')
  }

  return (
    <div className="min-h-screen bg-background text-foreground relative">
      {/* Animated gradient background */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[rgb(var(--brand-green-vibrant))] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob" />
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-[rgb(var(--brand-teal-deep))] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-2000" />
        <div className="absolute bottom-1/4 left-1/3 w-96 h-96 bg-[rgb(var(--brand-yellow-orange))] rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-blob animation-delay-4000" />
      </div>

      <div className="relative z-10">
        <Header />

        {/* Hero Section - Reimagined */}
        <motion.section
          className="relative overflow-hidden px-6 pt-16 pb-24"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
        >
          {/* Gradient blobs */}
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-radial from-[rgb(var(--brand-teal-deep))] to-transparent opacity-15 blur-3xl" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-radial from-[rgb(var(--brand-green-vibrant))] to-transparent opacity-15 blur-3xl" />
          </div>

          <div className="max-w-6xl mx-auto relative z-10">
            {/* Centered hero content */}
            <div className="text-center space-y-8 max-w-4xl mx-auto">
              {/* AI Clients indicator */}
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                className="flex justify-center"
              >
                <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-muted/50 border border-border/50">
                  <span className="text-sm text-muted-foreground">Works with your favorite AI</span>
                  <div className="flex items-center gap-2">
                    <img
                      src="/logos/ai-clients/claude.svg"
                      alt="Claude"
                      title="Claude"
                      className="w-5 h-5"
                    />
                    <img
                      src="/logos/ai-clients/chatgpt.svg"
                      alt="ChatGPT"
                      title="ChatGPT"
                      className="w-5 h-5"
                    />
                    <img
                      src="/logos/ai-clients/cursor.svg"
                      alt="Cursor"
                      title="Cursor"
                      className="w-5 h-5"
                    />
                    <img
                      src="/logos/ai-clients/windsurf.svg"
                      alt="Windsurf"
                      title="Windsurf"
                      className="w-5 h-5"
                    />
                    <img
                      src="/logos/ai-clients/github-copilot.svg"
                      alt="GitHub Copilot"
                      title="GitHub Copilot"
                      className="w-5 h-5"
                    />
                  </div>
                </div>
              </motion.div>

              {/* Main headline */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                className="space-y-4"
              >
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                  The First AI-Native Platform for
                  <span className="text-primary"> Sustainability Analysis</span>
                </h1>
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  From idea to environmental impact in seconds. Ask simple questions to calculate
                  footprints, compare materials and products, and explore scenarios—powered by
                  professional LCA databases.
                </p>
              </motion.div>

              {/* CTA buttons */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 justify-center"
              >
                <Button size="lg" className="text-lg px-8 h-12" onClick={handleStartFreeTrial}>
                  Start Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="text-lg px-8 h-12"
                  onClick={handleViewDocumentation}
                >
                  <BookOpen className="w-5 h-5 mr-2" />
                  Documentation
                </Button>
              </motion.div>

              {/* Trust indicators */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6, duration: 0.6 }}
                className="flex flex-wrap justify-center gap-6 pt-4"
              >
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-brand-green-vibrant" />
                  <span>31+ specialized tools</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-brand-teal-deep" />
                  <span>21,000+ Ecoinvent activities</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="w-4 h-4 text-brand-yellow-orange" />
                  <span>{WELCOME_CREDITS} free credits on signup</span>
                </div>
              </motion.div>

              {/* Product Hunt Badge */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.8, duration: 0.6 }}
                className="flex justify-center pt-6"
              >
                <a
                  href="https://www.producthunt.com/products/ecosemantic?embed=true&utm_source=badge-featured&utm_medium=badge&utm_source=badge-ecosemantic"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src="https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1050265&theme=light&t=1765816373888"
                    alt="EcoSemantic - Science-backed environmental analysis via AI conversation | Product Hunt"
                    style={{ width: '250px', height: '54px' }}
                    width="250"
                    height="54"
                  />
                </a>
              </motion.div>
            </div>

            {/* Interactive demo preview */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="mt-16 relative"
            >
              {/* Glow effect behind the card */}
              <div className="absolute inset-0 bg-gradient-to-r from-[rgb(var(--brand-teal-deep))]/20 via-[rgb(var(--brand-green-vibrant))]/20 to-[rgb(var(--brand-yellow-orange))]/20 blur-3xl -z-10" />

              <div className="bg-card/80 backdrop-blur-sm border rounded-2xl p-4 sm:p-6 shadow-2xl max-w-4xl mx-auto">
                {/* Terminal header */}
                <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className="flex gap-1.5 sm:gap-2 flex-shrink-0">
                      <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[rgb(var(--brand-red-deep))]" />
                      <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[rgb(var(--brand-yellow))]" />
                      <div className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[rgb(var(--brand-green-vibrant))]" />
                    </div>
                    <span className="text-xs sm:text-sm text-muted-foreground truncate">
                      AI Chat + EcoSemantic MCP
                    </span>
                  </div>
                  <Badge
                    variant="outline"
                    className="border-brand-teal-deep text-brand-teal-deep flex-shrink-0 text-[10px] sm:text-xs"
                  >
                    <Plug className="w-2.5 h-2.5 sm:w-3 sm:h-3 mr-1" />
                    Connected
                  </Badge>
                </div>

                {/* Chat conversation */}
                <div className="space-y-4 font-mono text-sm">
                  {/* User message */}
                  <div className="flex gap-2 sm:gap-3">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] sm:text-xs">You</span>
                    </div>
                    <div className="bg-muted rounded-lg px-3 py-2 sm:px-4 sm:py-3 text-xs sm:text-sm">
                      What's the carbon footprint of producing 1 MWh of electricity from natural gas
                      in Germany?
                    </div>
                  </div>

                  {/* AI thinking with tool calls */}
                  <div className="flex gap-2 sm:gap-3">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <Bot className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
                    </div>
                    <div className="space-y-2 flex-1 min-w-0">
                      <div className="bg-muted/50 rounded-lg px-3 py-2 sm:px-4 sm:py-3 border border-dashed border-[rgb(var(--brand-teal-deep))]/30">
                        <div className="text-[10px] sm:text-xs text-muted-foreground mb-2">
                          Using EcoSemantic tools...
                        </div>
                        <div className="space-y-1.5 text-[10px] sm:text-xs">
                          <div className="flex items-start gap-1.5 sm:gap-2 text-[rgb(var(--brand-orange))]">
                            <Sparkles className="w-3 h-3 flex-shrink-0 mt-0.5" />
                            <span className="break-all">
                              search_activities("natural gas electricity", location="DE")
                            </span>
                          </div>
                          <div className="flex items-start gap-1.5 sm:gap-2 text-[rgb(var(--brand-teal-deep))]">
                            <Zap className="w-3 h-3 flex-shrink-0 mt-0.5" />
                            <span className="break-all">
                              calculate_standard(method="IPCC 2021 GWP100")
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* AI response */}
                      <div className="bg-card rounded-lg px-3 py-2 sm:px-4 sm:py-3 border text-xs sm:text-sm">
                        Based on Ecoinvent 3.11 data, electricity production from natural gas
                        (combined cycle) in Germany produces
                        <span className="font-bold text-[rgb(var(--brand-orange))]">
                          {' '}
                          412 kg CO₂-eq per MWh
                        </span>
                        . This includes upstream emissions from gas extraction and transport. Want
                        me to compare this with other sources?
                      </div>
                    </div>
                  </div>
                </div>

                {/* Speed indicator */}
                <div className="mt-4 sm:mt-6 pt-3 sm:pt-4 border-t border-dashed flex items-center justify-center gap-2 text-xs sm:text-sm text-muted-foreground">
                  <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-success" />
                  <span>
                    Query completed in{' '}
                    <span className="font-semibold text-foreground">2.3 seconds</span>
                  </span>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* How It Works Section */}
        <section className="px-6 py-20 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center space-y-4 mb-16"
            >
              <Badge className="badge-brand-teal">
                <Link2 className="w-3 h-3 mr-1" />
                How It Works
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold">Three Steps to AI-Powered LCA</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Connect EcoSemantic to your AI chat and start querying professional environmental
                data instantly.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: '01',
                  icon: Cpu,
                  title: 'Create Account',
                  description:
                    'Sign up for free and get instant access to the EcoSemantic MCP server. No credit card required.',
                  color: 'rgb(var(--brand-green-vibrant))',
                },
                {
                  step: '02',
                  icon: Plug,
                  title: 'Connect Your AI',
                  description:
                    'Add EcoSemantic to your AI chat platform. Works with any MCP-compatible client—desktop apps, web interfaces, or IDEs.',
                  color: 'rgb(var(--brand-teal-deep))',
                },
                {
                  step: '03',
                  icon: MessageSquare,
                  title: 'Ask Questions',
                  description:
                    'Query Ecoinvent data in natural language. Your AI uses our 31+ tools to search, calculate, and analyze environmental impacts.',
                  color: 'rgb(var(--brand-yellow-orange))',
                },
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.15, duration: 0.6 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  {/* Connector line */}
                  {index < 2 && (
                    <div className="hidden md:block absolute top-16 left-[60%] w-[80%] h-[2px] bg-gradient-to-r from-border to-transparent" />
                  )}

                  <Card className="h-full text-center border-2 hover:border-primary/50 transition-colors">
                    <CardHeader>
                      <div className="mx-auto mb-4 relative">
                        <div
                          className="w-16 h-16 rounded-2xl flex items-center justify-center"
                          style={{ backgroundColor: `${item.color}20` }}
                        >
                          <item.icon className="w-8 h-8" style={{ color: item.color }} />
                        </div>
                        <span
                          className="absolute -top-2 -right-2 text-xs font-bold px-2 py-1 rounded-full"
                          style={{ backgroundColor: item.color, color: 'white' }}
                        >
                          {item.step}
                        </span>
                      </div>
                      <CardTitle className="text-xl">{item.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base">{item.description}</CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* What Are Tool Calls Section */}
        <section className="px-6 py-20">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="space-y-6"
              >
                <Badge className="badge-brand-accent">
                  <Zap className="w-3 h-3 mr-1" />
                  Understanding Tool Calls
                </Badge>
                <h2 className="text-3xl sm:text-4xl font-bold">
                  You Pay for <span className="text-primary">Data Access</span>, Not Chat Messages
                </h2>
                <p className="text-lg text-muted-foreground">
                  Everything on EcoSemantic costs credits. Each tool call, each AI message in the
                  playground—one simple currency.
                </p>

                <div className="space-y-4">
                  <div className="flex gap-4 items-start">
                    <div className="w-10 h-10 rounded-lg bg-[rgb(var(--brand-green-vibrant))]/10 flex items-center justify-center flex-shrink-0">
                      <Database className="w-5 h-5 text-[rgb(var(--brand-green-vibrant))]" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Real Database Queries</h4>
                      <p className="text-sm text-muted-foreground">
                        Each tool call searches or calculates against the actual Ecoinvent
                        database—not cached or simplified data.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="w-10 h-10 rounded-lg bg-[rgb(var(--brand-teal-deep))]/10 flex items-center justify-center flex-shrink-0">
                      <Globe className="w-5 h-5 text-[rgb(var(--brand-teal-deep))]" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Use Anywhere</h4>
                      <p className="text-sm text-muted-foreground">
                        Your tool calls work the same whether you're in a web chat, desktop app, or
                        IDE. One subscription, any MCP client.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="w-10 h-10 rounded-lg bg-[rgb(var(--brand-yellow-orange))]/10 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-5 h-5 text-[rgb(var(--brand-yellow-orange))]" />
                    </div>
                    <div>
                      <h4 className="font-semibold">Flexible Usage</h4>
                      <p className="text-sm text-muted-foreground">
                        Start with {WELCOME_CREDITS} free credits. Buy more when you need them—
                        {creditUnit} credits for ${creditPrice}. No subscriptions, no monthly
                        commitments.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Tool calls visualization */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="bg-card border rounded-2xl p-6 space-y-4">
                  <h4 className="font-semibold text-center mb-6">
                    Example: One Question, Multiple Tools
                  </h4>

                  <div className="bg-muted rounded-lg p-4">
                    <p className="text-sm italic text-muted-foreground mb-4">
                      "Compare steel vs aluminum for a car chassis"
                    </p>

                    <div className="space-y-3">
                      {[
                        { tool: 'search_activities', desc: 'Find steel production', calls: 1 },
                        { tool: 'search_activities', desc: 'Find aluminum production', calls: 1 },
                        { tool: 'calculate_standard', desc: 'Steel GWP impact', calls: 1 },
                        { tool: 'calculate_standard', desc: 'Aluminum GWP impact', calls: 1 },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2">
                            <CircleDot className="w-3 h-3 text-[rgb(var(--brand-teal-deep))]" />
                            <code className="text-xs bg-background px-2 py-1 rounded">
                              {item.tool}
                            </code>
                            <span className="text-muted-foreground text-xs">{item.desc}</span>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {item.calls} credit
                          </Badge>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4 pt-4 border-t flex justify-between items-center">
                      <span className="text-sm font-medium">Total for this query:</span>
                      <Badge className="bg-primary">4 credits</Badge>
                    </div>
                  </div>

                  <p className="text-xs text-center text-muted-foreground">
                    Simple questions may use 1-2 credits. Complex analyses might use 5-10. You're
                    always in control.
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Speed Comparison Section */}
        <section className="px-6 py-20 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center space-y-4 mb-12"
            >
              <Badge className="badge-brand-green">
                <Clock className="w-3 h-3 mr-1" />
                Speed Matters
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold">From Hours to Seconds</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Traditional LCA workflows require specialized software and manual data entry.
                EcoSemantic brings the same professional data to a conversational interface.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Traditional approach */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-2 border-muted">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-muted-foreground">
                      <Clock className="w-5 h-5" />
                      Traditional LCA Software
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      {[
                        'Learn specialized software interface',
                        'Navigate database manually',
                        'Build product system model',
                        'Configure impact assessment',
                        'Run calculation & export results',
                      ].map((step, i) => (
                        <div
                          key={i}
                          className="flex items-center gap-3 text-sm text-muted-foreground"
                        >
                          <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs">
                            {i + 1}
                          </div>
                          <span>{step}</span>
                        </div>
                      ))}
                    </div>
                    <div className="pt-4 border-t">
                      <div className="text-2xl font-bold text-muted-foreground">2-4 hours</div>
                      <p className="text-sm text-muted-foreground">
                        for a simple product assessment
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* EcoSemantic approach */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Card className="h-full border-2 border-primary relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-ecosemantic" />
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-primary">
                      <Zap className="w-5 h-5" />
                      EcoSemantic + AI
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-3">
                      {[
                        'Ask your question in plain English',
                        'AI selects appropriate tools',
                        'Get results with full methodology',
                      ].map((step, i) => (
                        <div key={i} className="flex items-center gap-3 text-sm">
                          <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs text-primary font-bold">
                            {i + 1}
                          </div>
                          <span>{step}</span>
                        </div>
                      ))}
                    </div>
                    <div className="pt-4 border-t">
                      <div className="text-2xl font-bold text-primary">30 seconds</div>
                      <p className="text-sm text-muted-foreground">
                        same Ecoinvent data, conversational interface
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center text-sm text-muted-foreground mt-8 max-w-2xl mx-auto"
            >
              EcoSemantic doesn't replace LCA expertise—it amplifies it. You still need professional
              judgment for complex assessments. We just remove the software friction.
            </motion.p>
          </div>
        </section>

        {/* Video Demo Section */}
        <section className="px-6 py-20">
          <div className="max-w-5xl mx-auto">
            <motion.div
              className="text-center space-y-4 mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Badge className="badge-brand-teal">
                <Bot className="w-3 h-3 mr-1" />
                See It In Action
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold">Watch a Real LCA Query</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                See how professionals use AI to explore Ecoinvent's 21,000+ activities and generate
                impact assessments through natural conversation.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              viewport={{ once: true }}
              className="relative rounded-2xl overflow-hidden shadow-2xl border border-primary/20"
            >
              <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-ecosemantic z-10" />
              <div className="relative bg-black aspect-video">
                <video
                  className="absolute inset-0 w-full h-full"
                  controls
                  autoPlay
                  muted
                  loop
                  playsInline
                >
                  <source src="/video_sample_landing_01.mp4" type="video/mp4" />
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[rgb(var(--brand-teal-deep))]/30 to-[rgb(var(--brand-green-vibrant))]/30">
                    <div className="text-center space-y-4">
                      <div className="w-24 h-24 mx-auto bg-primary/30 rounded-full flex items-center justify-center backdrop-blur-sm">
                        <svg
                          className="w-12 h-12 text-white"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M8 5v14l11-7z" />
                        </svg>
                      </div>
                      <p className="text-white text-lg font-semibold">Demo Video</p>
                    </div>
                  </div>
                </video>
              </div>
            </motion.div>

            {/* Stats below video */}
            <motion.div
              className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              viewport={{ once: true }}
            >
              {[
                { value: '31+', label: 'MCP Tools' },
                { value: '6', label: 'Database Versions' },
                { value: '21K+', label: 'LCA Activities' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl sm:text-3xl font-bold text-primary">{stat.value}</div>
                  <p className="text-sm text-muted-foreground">{stat.label}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Features Grid */}
        <section id="features" className="px-6 py-20 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <motion.div
              className="text-center space-y-4 mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Badge className="bg-brand-orange text-white border-transparent">
                <Database className="w-3 h-3 mr-1" />
                Platform Capabilities
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold">
                Professional LCA Tools, AI Interface
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Everything you need for environmental impact analysis, accessible through natural
                language.
              </p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: Database,
                  title: 'Complete Ecoinvent Access',
                  description:
                    'All major database versions (3.6-3.12) with 21,000+ activities and 695,000+ environmental flows.',
                  color: 'rgb(var(--brand-teal-deep))',
                },
                {
                  icon: Zap,
                  title: '31+ Specialized Tools',
                  description:
                    'Search activities, calculate impacts, explore biosphere flows, build custom models, and more.',
                  color: 'rgb(var(--brand-yellow-orange))',
                },
                {
                  icon: Bot,
                  title: 'Natural Language Queries',
                  description:
                    'Ask questions in plain English. AI translates your intent into precise database operations.',
                  color: 'rgb(var(--brand-green-vibrant))',
                },
                {
                  icon: Terminal,
                  title: 'Neo4j Graph Database',
                  description:
                    'Explore supply chain relationships and environmental flows through graph queries.',
                  color: 'rgb(var(--brand-orange))',
                },
                {
                  icon: Globe,
                  title: 'Any MCP Client',
                  description:
                    'Works with desktop apps, web interfaces, IDEs—anywhere MCP is supported.',
                  color: 'rgb(var(--brand-teal))',
                },
                {
                  icon: Sparkles,
                  title: 'Custom LCA Projects',
                  description:
                    'Build and save custom activities, create complex product systems, store your models.',
                  color: 'rgb(var(--brand-green-yellow))',
                },
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full hover:shadow-lg transition-all hover:border-primary/30">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${feature.color}20` }}
                        >
                          <feature.icon className="w-5 h-5" style={{ color: feature.color }} />
                        </div>
                      </div>
                      <CardTitle className="text-lg">{feature.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription>{feature.description}</CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="px-6 py-20">
          <div className="max-w-5xl mx-auto">
            <motion.div
              className="text-center space-y-4 mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Badge className="bg-brand-green-yellow text-black border-transparent">
                <Coins className="w-3 h-3 mr-1" />
                Transparent Pricing
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold">One Currency. No Subscriptions.</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Everything on EcoSemantic costs credits. Start free, buy more when you need them.
                Full access to all tools and databases from day one.
              </p>
            </motion.div>

            {/* Main pricing cards */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              {/* Free credits card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Card className="h-full text-center border-border/50">
                  <CardHeader className="pb-4">
                    <div className="mx-auto w-10 h-10 rounded-full bg-[rgb(var(--brand-green-vibrant))]/10 flex items-center justify-center mb-3">
                      <Gift className="w-5 h-5 text-[rgb(var(--brand-green-vibrant))]" />
                    </div>
                    <CardTitle className="text-lg">Start Free</CardTitle>
                    <div className="flex items-baseline justify-center gap-1.5 mt-1">
                      <span className="text-4xl font-bold tracking-tight">{WELCOME_CREDITS}</span>
                      <span className="text-sm text-muted-foreground">credits</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">On signup, no credit card</p>
                  </CardHeader>
                  <CardContent className="space-y-5 pt-0">
                    <div className="space-y-2.5 text-sm text-left">
                      <div className="flex items-center gap-2.5">
                        <Check className="w-4 h-4 text-[rgb(var(--brand-green-vibrant))] flex-shrink-0" />
                        <span>All 31+ LCA tools unlocked</span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <Check className="w-4 h-4 text-[rgb(var(--brand-green-vibrant))] flex-shrink-0" />
                        <span>Ecoinvent 3.6–3.12</span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <Check className="w-4 h-4 text-[rgb(var(--brand-green-vibrant))] flex-shrink-0" />
                        <span>Any MCP client</span>
                      </div>
                    </div>
                    <Button className="w-full" size="lg" onClick={handleStartFreeTrial}>
                      Create Account
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Buy credits card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Card className="h-full text-center ring-2 ring-primary/80 shadow-lg shadow-primary/5 relative">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                    <Badge className="bg-primary text-primary-foreground px-3 py-1 text-xs font-medium shadow-sm">
                      Pay As You Go
                    </Badge>
                  </div>
                  <CardHeader className="pt-8 pb-4">
                    <div className="mx-auto w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                      <Coins className="w-5 h-5 text-primary" />
                    </div>
                    <CardTitle className="text-lg">Buy Credits</CardTitle>
                    <div className="flex items-baseline justify-center gap-1.5 mt-1">
                      <span className="text-4xl font-bold tracking-tight">${creditPrice}</span>
                      <span className="text-sm text-muted-foreground">/ {creditUnit} credits</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">1–50 units per purchase</p>
                  </CardHeader>
                  <CardContent className="space-y-5 pt-0">
                    <div className="space-y-2.5 text-sm text-left">
                      <div className="flex items-center gap-2.5">
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                        <span>Valid for 12 months</span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                        <span>No recurring charges</span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <Check className="w-4 h-4 text-primary flex-shrink-0" />
                        <span>Buy anytime from dashboard</span>
                      </div>
                    </div>
                    <Button className="w-full" size="lg" onClick={handleStartFreeTrial}>
                      Get Started
                      <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>

              {/* Enterprise card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Card className="h-full text-center border-border/50">
                  <CardHeader className="pb-4">
                    <div className="mx-auto w-10 h-10 rounded-full bg-[rgb(var(--brand-teal-deep))]/10 flex items-center justify-center mb-3">
                      <Globe className="w-5 h-5 text-[rgb(var(--brand-teal-deep))]" />
                    </div>
                    <CardTitle className="text-lg">Enterprise</CardTitle>
                    <div className="flex items-baseline justify-center gap-1.5 mt-1">
                      <span className="text-4xl font-bold tracking-tight">Custom</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      Volume pricing, monthly billing
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-5 pt-0">
                    <div className="space-y-2.5 text-sm text-left">
                      <div className="flex items-center gap-2.5">
                        <Check className="w-4 h-4 text-[rgb(var(--brand-teal-deep))] flex-shrink-0" />
                        <span>Monthly invoicing</span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <Check className="w-4 h-4 text-[rgb(var(--brand-teal-deep))] flex-shrink-0" />
                        <span>Volume discounts</span>
                      </div>
                      <div className="flex items-center gap-2.5">
                        <Check className="w-4 h-4 text-[rgb(var(--brand-teal-deep))] flex-shrink-0" />
                        <span>Dedicated support</span>
                      </div>
                    </div>
                    <Button
                      className="w-full"
                      variant="outline"
                      size="lg"
                      onClick={() => setShowContactSales(true)}
                    >
                      Contact Sales
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-6 py-20 bg-muted/30">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center space-y-6"
            >
              <h2 className="text-3xl sm:text-4xl font-bold">
                Ready to Transform Your LCA Workflow?
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Join sustainability professionals who are already using AI to accelerate their
                environmental analysis. Get {WELCOME_CREDITS} free credits on signup.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                <Button size="lg" className="text-lg px-8" onClick={handleStartFreeTrial}>
                  Start Free Account
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button size="lg" variant="outline" onClick={() => setShowContactSales(true)}>
                  Contact Sales
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Footer */}
        <footer className="px-6 py-12 border-t">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
              <div
                className="flex items-center space-x-2 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={scrollToTop}
              >
                <Logo size={32} showText={true} />
              </div>

              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8">
                <a
                  href="/privacy"
                  className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                >
                  Privacy
                </a>
                <a
                  href="/terms"
                  className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                >
                  Terms
                </a>
                <a
                  href="/cookies"
                  className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                >
                  Cookies
                </a>
                <a
                  href="mailto:support@ecosemantic.com"
                  className="text-muted-foreground hover:text-foreground transition-colors text-sm"
                >
                  Support
                </a>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t text-center text-sm text-muted-foreground">
              <p>© 2025 EcoSemantic by Kimel Tech SpA. All rights reserved.</p>
              <p className="mt-2">The first MCP server for AI-powered Life Cycle Assessment.</p>
            </div>
          </div>
        </footer>

        {/* Dialogs */}
        <ContactSalesDialog open={showContactSales} onOpenChange={setShowContactSales} />
      </div>

      {/* Scroll to Top */}
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{
          opacity: showScrollTop ? 1 : 0,
          scale: showScrollTop ? 1 : 0,
        }}
        transition={{ duration: 0.3 }}
        className="fixed bottom-8 right-8 z-50"
      >
        <Button onClick={scrollToTop} size="icon" className="h-12 w-12 rounded-full shadow-lg">
          <ArrowUp className="h-5 w-5" />
        </Button>
      </motion.div>
    </div>
  )
}

export default LandingPageV2
